import { create } from "@/lib/create-safe-store";
import { getStorage, sanitizeMessagesForStorage } from "@/lib/storage";
import type { Message, Reaction } from "./types";

// Read receipt: userId → lastReadMessageId per conversation
type ReadReceipts = Record<string, Record<string, string>>;

interface MessageState {
  messagesByConversation: Record<string, Message[]>;
  transcriptions: Record<string, string>;
  readReceipts: ReadReceipts;
  setMessages: (conversationId: string, messages: Message[]) => void;
  prependMessages: (conversationId: string, messages: Message[]) => void;
  addMessage: (conversationId: string, message: Message) => void;
  replaceMessage: (conversationId: string, targetId: string, message: Message) => void;
  updateMessage: (conversationId: string, message: Message) => void;
  updateMessagePreservingLocalUploads: (conversationId: string, message: Message) => void;
  updateReactions: (conversationId: string, messageId: string, reactions: Reaction[]) => void;
  removeMessage: (conversationId: string, messageId: string) => void;
  setTranscription: (messageId: string, text: string) => void;
  setReadReceipt: (conversationId: string, userId: string, messageId: string) => void;
  setReadReceipts: (conversationId: string, receipts: Record<string, string>) => void;
}

function isOptimisticMessageId(id: string) {
  return id.startsWith("pending_") || id.startsWith("pending_audio_");
}

function compareMessages(a: Message, b: Message) {
  // Optimistic ('sending') messages always sort to the very end. Without
  // this guard, a slight client/server clock skew can place an optimistic
  // bubble *between* already-confirmed messages — visible to the user as a
  // jump up/down at the moment the server response (with its own
  // created_at) replaces the optimistic entry.
  const aOpt = isOptimisticMessageId(a.id);
  const bOpt = isOptimisticMessageId(b.id);
  if (aOpt !== bOpt) return aOpt ? 1 : -1;

  const createdAtDiff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  if (createdAtDiff !== 0) return createdAtDiff;
  return a.id.localeCompare(b.id);
}

function normalizeMessages(messages: Message[]) {
  const byId = new Map<string, Message>();
  for (const message of messages) {
    byId.set(message.id, message);
  }
  return [...byId.values()].sort(compareMessages);
}

function persistMessages(conversationId: string, messages: Message[]) {
  getStorage().saveMessages(
    conversationId,
    sanitizeMessagesForStorage(messages),
  ).catch(() => {});
}

function mergeIncomingMessageWithLocalUploads(
  currentMessage: Message | undefined,
  incomingMessage: Message,
): Message {
  const currentLocalAttachments = currentMessage?.metadata?.local_attachments ?? [];
  if (!currentMessage || currentLocalAttachments.length === 0) {
    return incomingMessage;
  }

  const incomingAttachmentCount = incomingMessage.attachments?.length ?? 0;
  const shouldKeepLocalBundle =
    (currentMessage.send_status === "sending" ||
      currentMessage.send_status === "failed") &&
    incomingAttachmentCount < currentLocalAttachments.length;

  if (!shouldKeepLocalBundle) {
    return incomingMessage;
  }

  // "failed" is a terminal status explicitly set by the upload pipeline
  // when all media uploads gave up. Don't let it get reverted to
  // "sending" by a stale currentMessage. Bare "sent", on the other hand,
  // can come from the WebSocket addMessage path which promotes the
  // incoming message to "sent" optimistically — in that case we keep
  // showing "sending" until every local_attachment finishes uploading,
  // so the user can still see per-file progress.
  const incomingStatus = incomingMessage.send_status;
  const allLocalUploadsDone = currentLocalAttachments.every(
    (attachment) => attachment.status === "done",
  );
  const preserveIncomingStatus =
    incomingStatus === "failed" ||
    (incomingStatus === "sent" && allLocalUploadsDone);

  return {
    ...incomingMessage,
    metadata: {
      ...(incomingMessage.metadata ?? {}),
      local_attachments: currentLocalAttachments,
    },
    send_status: preserveIncomingStatus ? incomingStatus : currentMessage.send_status,
    send_error: preserveIncomingStatus ? incomingMessage.send_error : currentMessage.send_error,
  };
}

export const useMessageStore = create<MessageState>((set) => ({
  messagesByConversation: {},
  transcriptions: {},
  readReceipts: {},

  setMessages: (conversationId, messages) => {
    const next = normalizeMessages(messages);
    set((s) => ({
      messagesByConversation: { ...s.messagesByConversation, [conversationId]: next },
    }));
    persistMessages(conversationId, next);
  },

  prependMessages: (conversationId, messages) =>
    set((s) => {
      const next = normalizeMessages([
        ...messages,
        ...(s.messagesByConversation[conversationId] || []),
      ]);
      persistMessages(conversationId, next);
      return {
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: next,
        },
      };
    }),

  addMessage: (conversationId, message) =>
    set((s) => {
      const existing = s.messagesByConversation[conversationId] || [];

      // 1) Skip exact-id duplicates (websocket replay, double-fire).
      if (existing.some((item) => item.id === message.id)) {
        return s;
      }

      const isOptimistic = (id: string) =>
        id.startsWith("pending_") || id.startsWith("pending_audio_");

      // 2) Cross-event coordination for outgoing messages: when the WebSocket
      //    'new_message' arrives before the HTTP response of our send, we
      //    already have an optimistic bubble (pending_NNN) with the same
      //    sender + content. Swap it in-place to the real message instead of
      //    appending a duplicate row that flashes for ~50ms before the HTTP
      //    replaceMessage cleans it up. Local-attachment uploads keep their
      //    preview/progress via mergeIncomingMessageWithLocalUploads.
      if (!isOptimistic(message.id)) {
        const optimisticIdx = existing.findIndex(
          (item) =>
            isOptimistic(item.id) &&
            item.sender_id === message.sender_id &&
            item.type === message.type &&
            (item.content ?? "") === (message.content ?? ""),
        );
        if (optimisticIdx >= 0) {
          const previous = existing[optimisticIdx];
          const mergedBase: Message = {
            ...message,
            local_id: message.local_id ?? previous.local_id ?? previous.id,
            // Promote to "sent" — the websocket arrived which means the
            // server already accepted it. The HTTP-driven replaceMessage
            // will run shortly and overwrite this with the same payload.
            send_status: "sent",
            send_error: null,
          };
          const merged = mergeIncomingMessageWithLocalUploads(
            previous,
            mergedBase,
          );
          const updated = existing.slice();
          updated[optimisticIdx] = merged;
          const next = normalizeMessages(updated);
          persistMessages(conversationId, next);
          return {
            messagesByConversation: {
              ...s.messagesByConversation,
              [conversationId]: next,
            },
          };
        }
      }

      const next = normalizeMessages([...existing, message]);
      persistMessages(conversationId, next);
      return {
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: next,
        },
      };
    }),

  replaceMessage: (conversationId, targetId, message) =>
    set((s) => {
      const existing = s.messagesByConversation[conversationId] || [];
      const previous = existing.find((item) => item.id === targetId);

      if (!previous) {
        // Optimistic bubble was already merged into the real message by an
        // earlier websocket new_message event (or a re-sync). If we tried to
        // replace anyway the filter below would *remove* the already-merged
        // real row (because its id === message.id but its id !== targetId).
        // The message is already in the list — just make sure it carries the
        // local_id so React keys stay stable across the optimistic→real swap.
        const idxByRealId = existing.findIndex((item) => item.id === message.id);
        if (idxByRealId >= 0) {
          const current = existing[idxByRealId];
          if (current.local_id) return s;
          const updated = existing.slice();
          updated[idxByRealId] = { ...current, local_id: targetId };
          const next = normalizeMessages(updated);
          persistMessages(conversationId, next);
          return {
            messagesByConversation: {
              ...s.messagesByConversation,
              [conversationId]: next,
            },
          };
        }
        // Neither optimistic nor real present — append the message (lossless).
        const next = normalizeMessages([
          ...existing,
          { ...message, local_id: message.local_id ?? targetId },
        ]);
        persistMessages(conversationId, next);
        return {
          messagesByConversation: {
            ...s.messagesByConversation,
            [conversationId]: next,
          },
        };
      }

      const mergedBase: Message = {
        ...message,
        local_id: message.local_id ?? previous.local_id ?? targetId,
      };
      // Preserve in-flight local upload previews/progress when the HTTP
      // response replaces the optimistic bubble before the per-attachment
      // confirmUpload calls have completed.
      const merged = mergeIncomingMessageWithLocalUploads(previous, mergedBase);
      const next = normalizeMessages(
        existing
          .filter((item) => item.id !== merged.id || item.id === targetId)
          .map((item) => (item.id === targetId ? merged : item)),
      );
      persistMessages(conversationId, next);
      return {
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: next,
        },
      };
    }),

  updateMessage: (conversationId, message) =>
    set((s) => {
      const next = normalizeMessages(
        (s.messagesByConversation[conversationId] || []).map((m) =>
          m.id === message.id ? message : m,
        ),
      );
      persistMessages(conversationId, next);
      return {
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: next,
        },
      };
    }),

  updateMessagePreservingLocalUploads: (conversationId, message) =>
    set((s) => {
      const currentMessage = (s.messagesByConversation[conversationId] || []).find(
        (storedMessage) => storedMessage.id === message.id,
      );
      const mergedMessage = mergeIncomingMessageWithLocalUploads(currentMessage, message);
      const next = normalizeMessages(
        (s.messagesByConversation[conversationId] || []).map((m) =>
          m.id === message.id ? mergedMessage : m,
        ),
      );
      persistMessages(conversationId, next);
      return {
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: next,
        },
      };
    }),

  updateReactions: (conversationId, messageId, reactions) =>
    set((s) => {
      const next = normalizeMessages(
        (s.messagesByConversation[conversationId] || []).map((m) =>
          m.id === messageId ? { ...m, reactions } : m,
        ),
      );
      persistMessages(conversationId, next);
      return {
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: next,
        },
      };
    }),

  removeMessage: (conversationId, messageId) =>
    set((s) => {
      const next = (s.messagesByConversation[conversationId] || []).filter(
        (m) => m.id !== messageId,
      );
      persistMessages(conversationId, next);
      return {
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: next,
        },
      };
    }),

  setTranscription: (messageId, text) =>
    set((s) => ({
      transcriptions: { ...s.transcriptions, [messageId]: text },
    })),

  setReadReceipt: (conversationId, userId, messageId) =>
    set((s) => ({
      readReceipts: {
        ...s.readReceipts,
        [conversationId]: {
          ...(s.readReceipts[conversationId] || {}),
          [userId]: messageId,
        },
      },
    })),

  setReadReceipts: (conversationId, receipts) =>
    set((s) => ({
      readReceipts: {
        ...s.readReceipts,
        [conversationId]: receipts,
      },
    })),
}));
